import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import LabHeader from './LabHeader';

interface LabExercisePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  exerciseId: string;
}

interface LabExercise {
  id: string;
  title: string;
  description: string;
  content: string;
}

export default function LabExercisePreviewModal({
  isOpen,
  onClose,
  exerciseId
}: LabExercisePreviewModalProps) {
  const [exercise, setExercise] = useState<LabExercise | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && exerciseId) {
      fetchExercise();
    }
  }, [isOpen, exerciseId]);

  const fetchExercise = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('lab_exercises')
        .select('*')
        .eq('id', exerciseId)
        .single();
      
      if (error) throw error;
      setExercise(data);
    } catch (err) {
      console.error('Error fetching lab exercise:', err);
      setError('Failed to load lab exercise');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Lab Exercise Preview</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F98B3D]"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          ) : exercise ? (
            <div className="lab-exercise-content">
              <LabHeader title={exercise.title} className="mt-0 mb-4" />
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: exercise.content }} />
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Lab exercise not found</p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
}